import List from 'components/Common/List'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Sheet from 'components/utils/Sheet'
import React, { useMemo } from 'react'
import MerchandiseOfferCarousel from '../MerchandiseCarousels/MerchandiseOfferCarousel'
import MerchandiseRecentlyViewedCarousel from '../MerchandiseCarousels/MerchandiseRecentlyViewedCarousel'
import LayoutContainer from 'components/Common/LayoutContainer'
import useMerchandiseCategory from 'hooks/Merchandise/useMerchandiseCategory'
import useMerchandiseTopCategories from 'hooks/Merchandise/useMerchandiseTopCategories'
import { take } from 'lib/array/arrayUtils'
import useMerchandiseCategories from 'hooks/Merchandise/useMerchandiseCategories'
import MerchandiseBestOfBrandCarousel from '../MerchandiseCarousels/MerchandiseBestOfBrandCarousel'
import MerchandiseBestOfCategoryCarousel from '../MerchandiseCarousels/MerchandiseBestOfCategoryCarousel'
import TextLink from 'LuxKit/TextLink'
import MerchandiseSearchPageBreadcrumbs from './MerchandiseSearchPageBreadcrumbs'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'

export const FullWidthContainer = styled.div`
  padding-left: ${rem(16)};
  padding-right: ${rem(16)};
  width: 100%;
  
  > * {
    margin-left: 0;
    margin-right: auto;
  }

  ${mediaQueryUp.tablet} {
    padding-left: ${rem(60)};
    padding-right: ${rem(60)};
  }
`

const popularProductsFilters: App.MerchandiseFilters = {
  sortBy: 'recommended',
}

const FIXED_BRAND_CAROUSEL = 'Apple'
const FIXED_CATEGORY_CAROUSEL = 'Home & Garden'

interface Props {
  search?: string
  category?: App.MerchandiseCategory
  brand?: App.MerchandiseBrand
  /**
   * Whether to show breadcrumbs at the top
   * @default true
   */
  showBreadcrumbs?: boolean
}

function MerchandiseSearchPageNoResults(props: Props) {
  const { search, category, brand, showBreadcrumbs = true } = props

  const [parentCategory] = useMerchandiseCategory({ id: category?.parentId })
  const [topCategories] = useMerchandiseTopCategories()
  const [childCategories] = useMerchandiseCategories({
    categoryIds: category?.childCategoryIds,
  }, { disabled: !category?.childCategoryIds })

  const popularFilters = useMemo((): App.MerchandiseFilters | undefined => {
    if (category) {
      if (category.parentId) {
        return {
          categories: [category.parentId],
        }
      } else if (topCategories.length > 0) {
        const aTopCategory = topCategories.find(cat => cat.id !== category.id)
        if (aTopCategory) {
          return {
            // just take the first top category that isn't ours
            categories: [aTopCategory.id],
          }
        }
      }
    }
  }, [category, topCategories])

  const relatedCategoryFilters = useMemo(() => {
    return childCategories.map(cat => ({
      name: cat.name,
      filter: {
        categories: [cat.id],
      },
    }))
  }, [childCategories])

  const trackingPageName = 'Search page no results'
  const searchForLabel = category?.name ?? brand?.name ?? search

  return <>
    {showBreadcrumbs && <FullWidthContainer>
      <MerchandiseSearchPageBreadcrumbs />
    </FullWidthContainer>}
    <LayoutContainer>
      <Sheet padding="20 0 64 0">
        <VerticalSpacer gap={32}>
          <Heading variant="heading2">
            No results found
            {!!searchForLabel && <>&nbsp;for &apos;{searchForLabel}&apos;</>}
          </Heading>
          <VerticalSpacer gap={8}>
            <BodyText variant="large">
              It might&apos;ve moved or is no longer available.
            </BodyText>
            <BodyText variant="large">
              <List>
                <li>Check your spelling or;</li>
                <li>Try searching by product name or category</li>
                <li>You can also <TextLink to="/shop/categories">shop by category</TextLink> or <TextLink to="/shop/brands">shop by brand</TextLink></li>
              </List>
            </BodyText>
            <BodyText variant="large">
              There&apos;s plenty more to discover in Luxury Escapes Shop.
            </BodyText>
          </VerticalSpacer>
        </VerticalSpacer>
      </Sheet>
    </LayoutContainer>
    <MerchandiseRecentlyViewedCarousel trackingPageName={trackingPageName} />
    <MerchandiseOfferCarousel
      title={<>Top products <em>for you</em></>}
      filters={popularProductsFilters}
      viewAllTo="/search/shop"
      trackingListName="Top products for you"
      trackingPageName={trackingPageName}
    />
    <MerchandiseBestOfBrandCarousel
      trackingPageName={trackingPageName}
      brandName={FIXED_BRAND_CAROUSEL}
    />
    <MerchandiseBestOfCategoryCarousel
      trackingPageName={trackingPageName}
      categoryName={FIXED_CATEGORY_CAROUSEL}
    />
    {take(relatedCategoryFilters, 2).map(filter => <MerchandiseOfferCarousel
      key={filter.name}
      title={`Explore more from ${filter.name}`}
      filters={filter.filter}
      trackingListName={`Explore more from ${filter.name}`}
      trackingPageName={trackingPageName}
    />)}
    {parentCategory && <MerchandiseOfferCarousel
      title={`Popular in ${parentCategory.name}`}
      filters={popularFilters}
      trackingListName={`Popular in ${parentCategory.name}`}
      trackingPageName={trackingPageName}
    />}
  </>
}

export default MerchandiseSearchPageNoResults
